/**
 * Ler TODAS as linhas de uma consulta, página a página.
 *
 * O PostgREST corta a resposta no `max_rows` sem avisar: a consulta volta com
 * sucesso e com menos linhas do que existem. Quem soma, conta ou procura um id
 * nessa resposta erra em silêncio assim que a clínica cresce. Aqui a leitura
 * pede faixas (`range`) até vir uma incompleta.
 */

export type PaginaDeLinhas<T> = (de: number, ate: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>;

export async function lerTudo<T>(pagina: PaginaDeLinhas<T>, tamanho = 1000): Promise<T[]> {
    const linhas: T[] = [];
    for (let de = 0; ; de += tamanho) {
        const { data, error } = await pagina(de, de + tamanho - 1);
        if (error) throw new Error(error.message);
        const lote = data ?? [];
        linhas.push(...lote);
        if (lote.length < tamanho) return linhas;
    }
}

/**
 * `lerTudo` na forma de resposta do supabase-js: o `erro(...)` de cada leitura
 * continua dando o contexto, e a listagem para de ser cortada em silêncio no
 * `max_rows` do PostgREST (1.000 linhas) quando a clínica cresce.
 */
export async function lerTodasAsLinhas<T>(pagina: PaginaDeLinhas<T>, tamanho = 1000): Promise<{ data: T[] | null; error: { message: string } | null }> {
    try {
        return { data: await lerTudo(pagina, tamanho), error: null };
    } catch (e) {
        return { data: null, error: { message: e instanceof Error ? e.message : String(e) } };
    }
}

/** A faixa `range` de uma página de tela: `pagina` começa em 1. */
export function faixaDaPagina(pagina: number | undefined, porPagina: number): { de: number; ate: number; pagina: number } {
    const p = Math.max(1, Math.floor(pagina ?? 1));
    return { de: (p - 1) * porPagina, ate: p * porPagina - 1, pagina: p };
}
